import { useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  KeyRound,
  Webhook as WebhookIcon,
  Copy,
  Trash2,
  Plus,
  Send,
  ShieldCheck,
  AlertTriangle,
  Power,
  BookOpen,
  Repeat,
  Check,
  ChevronDown,
  Code2,
  Plug,
  CheckCircle2,
  ClipboardList,
  Rocket,
  Workflow,
  FlaskConical,
} from 'lucide-react';

import {
  listApiKeys,
  createApiKey,
  deleteApiKey,
  listWebhooks,
  createWebhook,
  updateWebhook,
  deleteWebhook,
  testWebhook,
  type ApiKey,
  type WebhookEvent,
} from '@/services/integrations';
import { updateMe } from '@/services/profile';
import { useAuthStore } from '@/store/auth.store';
import { PageHeader, Spinner } from '@/components/fields';
import ErpLogPanel from '@/components/ErpLogPanel';
import { dateTime } from '@/lib/format';

const ALL_EVENTS: WebhookEvent[] = ['order.created', 'order.updated'];
const apiBase = `${typeof window !== 'undefined' ? window.location.origin : ''}/api/supplier`;
const DISABLED = 'disabled:opacity-100 disabled:bg-ink/10 disabled:text-ink/40 disabled:cursor-not-allowed';

function copy(text: string) {
  navigator.clipboard
    ?.writeText(text)
    .then(() => toast.success('Скопировано'))
    .catch(() => toast.error('Не удалось скопировать'));
}

/* ─── Карточка-шаг ───────────────────────────────────────────────────── */

function StepCard({
  n,
  title,
  description,
  done,
  optional,
  children,
}: {
  n: number;
  title: string;
  description?: string;
  done?: boolean;
  optional?: boolean;
  children: ReactNode;
}) {
  return (
    <section className="card p-5">
      <div className="flex items-start gap-3.5">
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-sm font-bold ${
            done
              ? 'bg-emerald-500 text-white'
              : optional
                ? 'bg-ink/10 text-ink/50'
                : 'bg-orange text-white'
          }`}
        >
          {done ? <Check size={17} /> : n}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-ink">{title}</h3>
            {optional && (
              <span className="badge bg-ink/5 text-ink/45">необязательно</span>
            )}
            {done && (
              <span className="badge bg-emerald-50 text-emerald-700">готово</span>
            )}
          </div>
          {description && <p className="text-sm text-ink/55 mt-0.5">{description}</p>}
          <div className="mt-4">{children}</div>
        </div>
      </div>
    </section>
  );
}

function SecretBanner({
  label,
  value,
  onHide,
}: {
  label: string;
  value: string;
  onHide: () => void;
}) {
  return (
    <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3.5">
      <div className="flex items-center gap-2 text-sm font-medium text-emerald-800">
        <ShieldCheck size={15} /> {label}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <code className="flex-1 break-all rounded bg-white border border-emerald-200 px-3 py-2 text-sm text-ink">
          {value}
        </code>
        <button className="btn-primary shrink-0" onClick={() => copy(value)}>
          <Copy size={15} /> Копировать
        </button>
      </div>
      <button className="mt-2 text-xs text-emerald-700 hover:underline" onClick={onHide}>
        Я сохранил, скрыть
      </button>
    </div>
  );
}

/* ─── Экран ──────────────────────────────────────────────────────────── */

export default function Integrations() {
  const { data: keys = [], isLoading: keysLoading } = useQuery({
    queryKey: ['api-keys'],
    queryFn: listApiKeys,
  });

  const hasKey = keys.length > 0;
  const usedAt = keys
    .map((k) => k.lastUsedAt)
    .filter(Boolean)
    .sort()
    .at(-1);

  return (
    <div>
      <PageHeader
        title="Интеграции"
        subtitle="Подключите вашу программу учёта (1С, ERP, складскую) — заявки, каталог и долги будут синхронизироваться автоматически"
      />

      <div className="space-y-5">
        <ConnectionHero loading={keysLoading} hasKey={hasKey} usedAt={usedAt} />
        <ApiKeysStep keys={keys} loading={keysLoading} />
        <WebhooksStep />
        <OrdersModeCard />
        <HandoffStep hasKey={hasKey} />
        {/* Журнал показываем только когда обмен вообще возможен: до создания
            ключа он гарантированно пуст и лишь путал бы новичка. */}
        {hasKey && <ErpLogPanel />}
        <DeveloperModeCard />
        <DevReference />
      </div>
    </div>
  );
}

/* ─── Заявки ведутся полностью в 1С ──────────────────────────────────── */

function OrdersModeCard() {
  const supplier = useAuthStore((s) => s.supplier);
  const setSupplier = useAuthStore((s) => s.setSupplier);
  const [saving, setSaving] = useState(false);
  const checked = supplier?.ordersManagedInErp ?? false;

  const toggle = async () => {
    if (!supplier) return;
    setSaving(true);
    try {
      const updated = await updateMe({ ordersManagedInErp: !checked });
      setSupplier(updated);
      toast.success(!checked ? 'Включено' : 'Выключено');
    } catch {
      /* тост от интерсептора */
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="card p-5">
      <div className="flex items-start gap-3.5">
        <div className="w-9 h-9 rounded-lg bg-ink/5 text-ink/50 flex items-center justify-center shrink-0">
          <Workflow size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-ink">Заявки ведутся полностью в 1С</h3>
          <p className="text-sm text-ink/55 mt-0.5">
            Включите, если подтверждение, отклонение и отгрузку заявок ваша 1С делает сама через
            API. Кабинет спрячет эти кнопки на странице «Заявки», чтобы сотрудник случайно не
            продублировал вручную то, что 1С уже сделала. На сам обмен данными (получение заявок,
            вебхуки) не влияет — это только про интерфейс кабинета.
          </p>
          <label className="mt-3 inline-flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={checked}
              onChange={toggle}
              disabled={saving || !supplier}
              className="h-4 w-4 rounded border-line text-orange focus:ring-orange/30"
            />
            <span className="text-sm text-ink/80">{checked ? 'Включено' : 'Выключено'}</span>
          </label>
        </div>
      </div>
    </section>
  );
}

/* ─── Режим разработчика ─────────────────────────────────────────────── */

function DeveloperModeCard() {
  const supplier = useAuthStore((s) => s.supplier);
  const setSupplier = useAuthStore((s) => s.setSupplier);
  const [saving, setSaving] = useState(false);
  const checked = supplier?.erpDeveloperMode ?? false;

  const toggle = async () => {
    if (!supplier) return;
    if (
      !checked &&
      !confirm(
        'Поля, которые ведёт 1С (название, цена, единицы измерения и т.д.), станут ' +
          'редактируемыми в кабинете, а карточки из 1С — удаляемыми. Правку затрёт ближайший ' +
          'обмен с 1С, если она продолжит присылать то же поле; удалённую карточку он создаст ' +
          'заново по тому же внешнему ID. Включить?',
      )
    ) {
      return;
    }
    setSaving(true);
    try {
      const updated = await updateMe({ erpDeveloperMode: !checked });
      setSupplier(updated);
      toast.success(!checked ? 'Режим разработчика включён' : 'Режим разработчика выключен');
    } catch {
      /* тост от интерсептора */
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={`card p-5 border ${checked ? 'border-amber-300' : 'border-transparent'}`}>
      <div className="flex items-start gap-3.5">
        <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
          <FlaskConical size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-ink">Режим разработчика</h3>
            {checked && <span className="badge bg-amber-100 text-amber-700">включён</span>}
          </div>
          <p className="text-sm text-ink/55 mt-0.5">
            Временно снимает замок с полей, которые обычно ведёт 1С (название, цена, единицы
            измерения и другие — везде, где в кабинете стоит значок замка), и разрешает удалять
            карточки из 1С. Пригодится, чтобы быстро поправить ошибку или вычистить мусор, не
            трогая учётную систему. Ближайший обмен вернёт своё: правку затрёт, если 1С продолжит
            присылать то же поле, а удалённую карточку создаст заново по тому же внешнему ID.
            Чтобы убрать позицию навсегда, снимите её с выгрузки в 1С.
          </p>
          <label className="mt-3 inline-flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={checked}
              onChange={toggle}
              disabled={saving || !supplier}
              className="h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-300/40"
            />
            <span className="text-sm text-ink/80">{checked ? 'Включён' : 'Выключен'}</span>
          </label>
        </div>
      </div>
    </section>
  );
}

/* ─── Статус подключения ─────────────────────────────────────────────── */

function ConnectionHero({
  loading,
  hasKey,
  usedAt,
}: {
  loading: boolean;
  hasKey: boolean;
  usedAt?: string | null;
}) {
  if (loading) return null;

  let tone: string;
  let icon: ReactNode;
  let title: string;
  let text: string;

  if (!hasKey) {
    tone = 'border-line bg-paper';
    icon = <Plug size={22} className="text-ink/40" />;
    title = 'Программа ещё не подключена';
    text = 'Пройдите 3 простых шага ниже — это займёт пару минут.';
  } else if (!usedAt) {
    tone = 'border-blue-200 bg-blue-50';
    icon = <Rocket size={22} className="text-blue-500" />;
    title = 'Доступ создан — ждём первое подключение';
    text = 'Передайте данные специалисту по 1С (шаг 3). Как только программа сделает первый запрос — статус станет зелёным.';
  } else {
    tone = 'border-emerald-200 bg-emerald-50';
    icon = <CheckCircle2 size={22} className="text-emerald-500" />;
    title = 'Программа подключена и работает';
    text = `Последний обмен данными: ${dateTime(usedAt)}.`;
  }

  return (
    <div className={`card p-5 border ${tone}`}>
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-white/70 border border-white flex items-center justify-center shrink-0">
          {icon}
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-ink">{title}</div>
          <p className="text-sm text-ink/60 mt-0.5">{text}</p>
        </div>
      </div>
    </div>
  );
}

/* ─── Шаг 1. Ключ доступа ────────────────────────────────────────────── */

function ApiKeysStep({ keys, loading }: { keys: ApiKey[]; loading: boolean }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [freshSecret, setFreshSecret] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => createApiKey(name.trim()),
    onSuccess: (res) => {
      setFreshSecret(res.secret);
      setName('');
      qc.invalidateQueries({ queryKey: ['api-keys'] });
      toast.success('Ключ создан');
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteApiKey(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['api-keys'] });
      toast.success('Ключ удалён');
    },
  });

  return (
    <StepCard
      n={1}
      title="Создайте ключ доступа"
      description="Ключ — это как логин и пароль для вашей программы. По нему 1С получает доступ к вашим данным. Никому не показывайте его."
      done={keys.length > 0}
    >
      {freshSecret && (
        <SecretBanner
          label="Скопируйте ключ — он показывается только один раз"
          value={freshSecret}
          onHide={() => setFreshSecret(null)}
        />
      )}

      <form
        className="mb-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) create.mutate();
        }}
      >
        <span className="label">Название (чтобы вы не путались, если ключей несколько)</span>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            className="input flex-1"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Например: 1С Бухгалтерия"
            maxLength={80}
          />
          <button
            className={`btn-primary shrink-0 whitespace-nowrap ${DISABLED}`}
            disabled={!name.trim() || create.isPending}
          >
            <Plus size={16} /> Создать ключ
          </button>
        </div>
      </form>

      {loading ? (
        <Spinner label="Загрузка…" />
      ) : keys.length === 0 ? (
        <p className="text-sm text-ink/45">Ключей пока нет — создайте первый выше.</p>
      ) : (
        <div className="divide-y divide-line border-t border-line">
          {keys.map((k) => (
            <div key={k.id} className="py-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-ink/5 text-ink/40 flex items-center justify-center shrink-0">
                <KeyRound size={17} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-ink truncate">{k.name}</div>
                <div className="text-xs text-ink/45 mt-0.5">
                  <code className="text-ink/60">{k.keyPrefix}</code> · создан {dateTime(k.createdAt)}
                  {k.lastUsedAt ? (
                    <span className="text-emerald-600"> · активен, посл. запрос {dateTime(k.lastUsedAt)}</span>
                  ) : (
                    ' · ещё не использовался'
                  )}
                </div>
              </div>
              <button
                className="btn-ghost text-red-600 shrink-0"
                onClick={() => {
                  if (confirm(`Удалить ключ «${k.name}»? Программа на этом ключе перестанет работать.`))
                    remove.mutate(k.id);
                }}
                title="Удалить"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </StepCard>
  );
}

/* ─── Шаг 2. Уведомления (вебхуки) ───────────────────────────────────── */

function WebhooksStep() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['webhooks'], queryFn: listWebhooks });
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<WebhookEvent[]>([]);
  const [freshSecret, setFreshSecret] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => createWebhook({ url: url.trim(), events: events.length ? events : undefined }),
    onSuccess: (res) => {
      setFreshSecret(res.secret);
      setUrl('');
      setEvents([]);
      qc.invalidateQueries({ queryKey: ['webhooks'] });
      toast.success('Уведомление добавлено');
    },
  });

  const toggle = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      updateWebhook(id, { isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['webhooks'] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteWebhook(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['webhooks'] });
      toast.success('Уведомление удалено');
    },
  });

  const test = useMutation({
    mutationFn: (id: string) => testWebhook(id),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['webhooks'] });
      if (r.lastStatus && r.lastStatus >= 200 && r.lastStatus < 300)
        toast.success(`Доставлено: HTTP ${r.lastStatus}`);
      else toast.error(`Ошибка: ${r.lastError ?? r.lastStatus ?? 'нет ответа'}`);
    },
  });

  const hooks = data ?? [];

  function toggleEvent(ev: WebhookEvent) {
    setEvents((prev) => (prev.includes(ev) ? prev.filter((e) => e !== ev) : [...prev, ev]));
  }

  return (
    <StepCard
      n={2}
      title="Мгновенные уведомления о заказах"
      description="Чтобы новые заявки попадали в 1С сразу же. Можно пропустить — тогда программа будет забирать их сама каждые несколько минут."
      optional
      done={hooks.length > 0}
    >
      {freshSecret && (
        <SecretBanner
          label="Секрет для проверки подписи — сохраните, показывается один раз"
          value={freshSecret}
          onHide={() => setFreshSecret(null)}
        />
      )}

      <form
        className="mb-4 space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (url.trim()) create.mutate();
        }}
      >
        <div>
          <span className="label">Адрес, куда слать уведомления (даёт специалист 1С)</span>
          <input
            className="input"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://ваш-сервер-1с.ru/webhooks/resstro"
            type="url"
          />
        </div>
        <div>
          <span className="label">О чём уведомлять</span>
          <div className="flex flex-wrap items-center gap-2">
            {ALL_EVENTS.map((ev) => {
              const on = events.includes(ev);
              const human = ev === 'order.created' ? 'новая заявка' : 'изменение заявки';
              return (
                <button
                  type="button"
                  key={ev}
                  onClick={() => toggleEvent(ev)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium border transition ${
                    on
                      ? 'bg-orange/10 text-orange-700 border-orange/30'
                      : 'bg-white text-ink/55 border-line hover:bg-paper'
                  }`}
                  title={ev}
                >
                  {human}
                </button>
              );
            })}
            {events.length === 0 && (
              <span className="text-xs text-ink/40">не выбрано — будут все события</span>
            )}
          </div>
        </div>
        <button className={`btn-primary ${DISABLED}`} disabled={!url.trim() || create.isPending}>
          <Plus size={16} /> Добавить уведомление
        </button>
      </form>

      {isLoading ? (
        <Spinner label="Загрузка…" />
      ) : hooks.length === 0 ? (
        <p className="text-sm text-ink/45">Пока не настроено. Этот шаг можно пропустить.</p>
      ) : (
        <div className="divide-y divide-line border-t border-line">
          {hooks.map((h) => {
            const ok = h.lastStatus != null && h.lastStatus >= 200 && h.lastStatus < 300;
            return (
              <div key={h.id} className="py-3 flex items-start gap-3">
                <div
                  className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                    h.isActive ? 'bg-orange/10 text-orange' : 'bg-ink/5 text-ink/35'
                  }`}
                >
                  <WebhookIcon size={17} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-ink break-all">{h.url}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    {(h.events.length
                      ? h.events.map((e) => (e === 'order.created' ? 'новая заявка' : 'изменение'))
                      : (['все события'] as string[])
                    ).map((e) => (
                      <span key={e} className="badge bg-ink/5 text-ink/55">
                        {e}
                      </span>
                    ))}
                    {!h.isActive && <span className="badge bg-ink/5 text-ink/40">выключено</span>}
                  </div>
                  <div className="text-xs text-ink/45 mt-1">
                    {h.lastDeliveryAt ? (
                      <span
                        className={`inline-flex items-center gap-1 ${
                          ok ? 'text-emerald-600' : 'text-red-600'
                        }`}
                      >
                        {ok ? null : <AlertTriangle size={11} />}
                        последняя доставка {dateTime(h.lastDeliveryAt)} ·{' '}
                        {h.lastStatus ? `HTTP ${h.lastStatus}` : h.lastError ?? 'нет ответа'}
                        {h.failureCount > 0 && ` · ошибок подряд: ${h.failureCount}`}
                      </span>
                    ) : (
                      'ещё не отправлялось'
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    className="btn-ghost"
                    onClick={() => test.mutate(h.id)}
                    disabled={test.isPending}
                    title="Отправить тест"
                  >
                    <Send size={15} />
                  </button>
                  <button
                    className={`btn-ghost ${h.isActive ? 'text-ink/50' : 'text-emerald-600'}`}
                    onClick={() => toggle.mutate({ id: h.id, isActive: !h.isActive })}
                    title={h.isActive ? 'Выключить' : 'Включить'}
                  >
                    <Power size={15} />
                  </button>
                  <button
                    className="btn-ghost text-red-600"
                    onClick={() => {
                      if (confirm('Удалить уведомление?')) remove.mutate(h.id);
                    }}
                    title="Удалить"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </StepCard>
  );
}

/* ─── Шаг 3. Передать данные в 1С ────────────────────────────────────── */

function HandoffStep({ hasKey }: { hasKey: boolean }) {
  const handoffText = [
    'Интеграция с Resstro «Закуп»',
    `Базовый адрес API: ${apiBase}`,
    'Авторизация: заголовок X-API-Key: <ключ из кабинета, шаг 1>',
    'Форматы: деньги — копейки (8000 = 80 ₽); даты — ISO 8601 UTC; тело — JSON UTF-8',
    'Каталог: POST /products и /categories с полем externalId (GUID из 1С) — обновление без дублей',
    'Единицы измерения: ключ позиции — externalId номенклатуры + unitId единицы; цены и НДС передаются массивом units[]',
    'НДС: 0, 5, 7, 10, 22 и расчётные 105, 107, 110, 122 (принимаются строки вида «НДС10», «10/110»)',
    'Пачками: POST /products и /categories принимают массив (до 500), DELETE — массив ключей (до 1000); частичные ошибки — код 207',
    'Оплаты: POST /orders/:id/payments с полем clientRequestId — защита от двойной оплаты',
    'Заявки: GET /orders?status=NEW, смена статуса POST /orders/:id/status',
    'Инкремент: GET /sync (метки коллекций) + ?updatedSince=ISO в /orders, /products, /categories',
    'Кэш: ETag в ответе → If-None-Match в запросе → 304, если данные не менялись',
    'PATCH пишет только изменённые поля; применённые перечислены в X-Changed-Fields',
    'Ответ на запись по API-ключу — сводка { changed, updatedAt }; объект целиком — ?response=full',
  ].join('\n');

  return (
    <StepCard
      n={3}
      title="Передайте доступ специалисту по 1С"
      description="Не разбираетесь в технике? Просто отправьте эти данные тому, кто настраивает вашу 1С. Всё остальное сделает он."
    >
      <div className="rounded-lg bg-paper border border-line p-3.5 space-y-3">
        <div>
          <span className="label">Адрес для подключения</span>
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate rounded-lg bg-white border border-line px-3 py-2.5 text-sm text-ink/80">
              {apiBase}
            </code>
            <button className="btn-outline shrink-0" onClick={() => copy(apiBase)} title="Скопировать адрес">
              <Copy size={15} />
            </button>
          </div>
        </div>

        <div className="flex items-start gap-2 text-xs text-ink/55">
          <KeyRound size={14} className="shrink-0 mt-0.5 text-ink/40" />
          <span>
            Ключ доступа — из шага 1 (значение <code className="text-ink/70">rsk_live_…</code>). Он
            показывается один раз при создании; если потеряли — создайте новый.
          </span>
        </div>
      </div>

      <button
        className={`btn-primary mt-3 ${DISABLED}`}
        onClick={() => copy(handoffText)}
        disabled={!hasKey}
        title={hasKey ? 'Скопировать памятку' : 'Сначала создайте ключ (шаг 1)'}
      >
        <ClipboardList size={16} /> Скопировать памятку для интегратора
      </button>
      {!hasKey && (
        <p className="text-xs text-ink/40 mt-2">Кнопка станет активной после создания ключа в шаге 1.</p>
      )}
    </StepCard>
  );
}

/* ─── Технический раздел (свёрнут) ───────────────────────────────────── */

const ENDPOINTS: { m: string; path: string; desc: string }[] = [
  { m: 'GET', path: '/sync', desc: 'что менялось: метки коллекций + курсор' },
  { m: 'GET', path: '/orders?updatedSince=', desc: 'только изменённые заявки' },
  { m: 'POST', path: '/orders/:id/status', desc: 'подтвердить / отклонить / отгрузить' },
  { m: 'POST', path: '/orders/:id/payments', desc: 'отметить оплату долга' },
  { m: 'GET', path: '/debts', desc: 'долги по заведениям' },
  { m: 'POST', path: '/categories', desc: 'категория или массив (upsert по externalId)' },
  { m: 'POST', path: '/products', desc: 'товар с units[] или массив (upsert по externalId)' },
  { m: 'DELETE', path: '/products', desc: 'удалить пачкой: массив id или externalId' },
  { m: 'PATCH', path: '/me', desc: 'реквизиты: пишутся только изменённые поля' },
];

const METHOD_COLOR: Record<string, string> = {
  GET: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  POST: 'bg-orange/10 text-orange-700 border-orange/30',
  PATCH: 'bg-sky-50 text-sky-700 border-sky-200',
  DELETE: 'bg-red-50 text-red-700 border-red-200',
};

function DevReference() {
  const [open, setOpen] = useState(false);
  const curl = `curl -H "X-API-Key: rsk_live_ВАШ_КЛЮЧ" \\\n  ${apiBase}/me`;

  return (
    <section className="card overflow-hidden">
      <button
        className="w-full flex items-center gap-3 p-5 text-left hover:bg-paper transition"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="w-9 h-9 rounded-lg bg-ink/5 text-ink/50 flex items-center justify-center shrink-0">
          <Code2 size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-ink">Для специалиста: техническая справка</h3>
          <p className="text-sm text-ink/50 mt-0.5">
            Эндпоинты, форматы данных и правила идемпотентности. Обычному пользователю смотреть не нужно.
          </p>
        </div>
        <ChevronDown
          size={18}
          className={`text-ink/40 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="px-5 pb-5 border-t border-line pt-4 space-y-5">
          <div className="grid gap-2 sm:grid-cols-2">
            {[
              ['Заголовок', 'X-API-Key: rsk_live_… в каждом запросе'],
              ['Деньги', 'копейки, целое число (8000 = 80,00 ₽)'],
              ['Даты', 'ISO 8601, UTC (2026-07-18T12:30:00.000Z)'],
              ['Тело', 'JSON, кодировка UTF-8'],
            ].map(([k, v]) => (
              <div key={k} className="rounded-lg bg-paper border border-line px-3 py-2">
                <div className="text-xs font-semibold text-ink/70">{k}</div>
                <div className="text-xs text-ink/55 mt-0.5">{v}</div>
              </div>
            ))}
          </div>

          <div>
            <span className="label">Проверка доступа</span>
            <div className="flex items-start gap-2">
              <pre className="flex-1 overflow-x-auto rounded-lg bg-ink/[0.04] border border-line px-3 py-2.5 text-xs text-ink/80 whitespace-pre">
                {curl}
              </pre>
              <button className="btn-outline shrink-0" onClick={() => copy(curl)} title="Скопировать">
                <Copy size={15} />
              </button>
            </div>
          </div>

          <div>
            <span className="label">Частые эндпоинты</span>
            <div className="divide-y divide-line border-t border-line">
              {ENDPOINTS.map((e) => (
                <div key={`${e.m} ${e.path}`} className="py-2 flex items-center gap-3">
                  <span
                    className={`inline-block w-12 text-center text-[10px] font-bold uppercase rounded border px-1 py-0.5 shrink-0 ${
                      METHOD_COLOR[e.m] ?? 'bg-ink/5 text-ink/60 border-line'
                    }`}
                  >
                    {e.m}
                  </span>
                  <code className="text-xs text-ink/80 shrink-0">{e.path}</code>
                  <span className="text-xs text-ink/45 truncate">{e.desc}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-orange/25 bg-orange/[0.04] p-3.5">
            <div className="flex items-center gap-2 text-sm font-semibold text-ink">
              <Repeat size={15} className="text-orange" /> Идемпотентность (безопасный повтор)
            </div>
            <ul className="mt-2 space-y-1.5 text-xs text-ink/65">
              <li>
                <code className="text-ink/80">externalId</code> в{' '}
                <code className="text-ink/80">POST /products</code> и{' '}
                <code className="text-ink/80">/categories</code> — код номенклатуры из 1С (GUID).
                Повторная выгрузка обновляет карточку, а не плодит дубли.
              </li>
              <li>
                <code className="text-ink/80">clientRequestId</code> в{' '}
                <code className="text-ink/80">POST /orders/:id/payments</code> — уникальный ключ
                платежа. Повтор с тем же ключом не создаёт второй платёж.
              </li>
              <li>
                <code className="text-ink/80">PATCH</code> пишет только реально изменённые поля:
                можно прислать карточку целиком, служебные поля будут отброшены, а{' '}
                <code className="text-ink/80">updatedAt</code> не сдвинется. Применённые поля — в
                заголовке <code className="text-ink/80">X-Changed-Fields</code>.
              </li>
              <li>
                Ответ на запись по API-ключу — сводка{' '}
                <code className="text-ink/80">{'{ changed, updatedAt }'}</code> (у товаров и
                категорий ещё <code className="text-ink/80">id</code>,{' '}
                <code className="text-ink/80">externalId</code>,{' '}
                <code className="text-ink/80">created</code>), а не объект целиком. Нужен объект —{' '}
                <code className="text-ink/80">?response=full</code>.
              </li>
            </ul>
          </div>

          <div className="rounded-lg border border-line bg-paper p-3.5">
            <div className="flex items-center gap-2 text-sm font-semibold text-ink">
              <Repeat size={15} className="text-ink/40" /> Единицы измерения и цены
            </div>
            <ul className="mt-2 space-y-1.5 text-xs text-ink/65">
              <li>
                Позиция определяется парой <code className="text-ink/80">externalId</code>{' '}
                номенклатуры + <code className="text-ink/80">unitId</code> единицы. Новый{' '}
                <code className="text-ink/80">unitId</code> добавляет единицу к тому же товару, а не
                вторую карточку.
              </li>
              <li>
                Массив <code className="text-ink/80">units[]</code>: у каждой единицы свои{' '}
                <code className="text-ink/80">price</code>,{' '}
                <code className="text-ink/80">vatRate</code>,{' '}
                <code className="text-ink/80">ratio</code>. Основная (
                <code className="text-ink/80">isDefault</code>) — её цену видят заведения в списке.
              </li>
              <li>
                <code className="text-ink/80">unitsMode: "replace"</code> — список полный: единицы,
                которых в нём нет, гаснут (история заявок сохраняется).
              </li>
              <li>
                Ставки НДС: 0, 5, 7, 10, 22 и расчётные 105, 107, 110, 122. Строки{' '}
                <code className="text-ink/80">«НДС10»</code>,{' '}
                <code className="text-ink/80">«10/110»</code>,{' '}
                <code className="text-ink/80">«БезНДС»</code> распознаются.
              </li>
              <li>
                Что прислала 1С — в кабинете только читается (замок на поле). Правка такого поля
                вручную вернула бы прежнее значение при следующем обмене.
              </li>
            </ul>
          </div>

          <div className="rounded-lg border border-line bg-paper p-3.5">
            <div className="flex items-center gap-2 text-sm font-semibold text-ink">
              <Repeat size={15} className="text-ink/40" /> Инкрементальный обмен (не качать одно и
              то же)
            </div>
            <ul className="mt-2 space-y-1.5 text-xs text-ink/65">
              <li>
                <code className="text-ink/80">GET /sync</code> — одна дешёвая проверка: сколько
                записей и когда менялись. Дальше тянуть только изменившееся.
              </li>
              <li>
                <code className="text-ink/80">?updatedSince=ISO</code> в{' '}
                <code className="text-ink/80">/products</code>,{' '}
                <code className="text-ink/80">/categories</code>,{' '}
                <code className="text-ink/80">/orders</code>. Курсор берите из ответа —{' '}
                <code className="text-ink/80">syncTime</code> или заголовок{' '}
                <code className="text-ink/80">X-Sync-Time</code>.
              </li>
              <li>
                <code className="text-ink/80">If-None-Match</code> с сохранённым{' '}
                <code className="text-ink/80">ETag</code> → <code className="text-ink/80">304</code>{' '}
                без тела, если данные не менялись.
              </li>
            </ul>
          </div>

          <div className="flex items-center gap-2 text-xs text-ink/45">
            <BookOpen size={14} /> Полная документация интеграции — раздел <code>docs/1C</code> в
            составе проекта.
          </div>
        </div>
      )}
    </section>
  );
}
